/**
 * Where you're signed in (2026-09-28, module 1.5.0) — pure words for the /security list.
 * Times are the VIEWER's local time: this is the person's own activity on their own devices.
 */
import type { SignInSessionView } from "../../../generated/graphql";
import { authWords } from "../components/ui/AuthStatusPill";
import { FACTOR_TYPES, signedOutAtWords } from "./assurance";

/** The server moves `lastSeenAt` at most every 5 minutes, so "now" can only mean "within 5 minutes". */
export const LAST_SEEN_PRECISION_MINUTES = 5;
/** The backend returns at most this many; the card says so if it ever hits it. */
export const SESSIONS_LIST_CAP = 200;

/** "Active in the last 5 minutes" · "Last active 12 minutes ago" · "Last active 3 hours ago" · "Last active yesterday, 9:05 am" · "Last active Sat 26 Sept, 9:05 am". */
export function lastActiveWords(value: string | Date | null | undefined, now: Date = new Date()): string {
  if (!value) return "Not used yet";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "Not used yet";
  const minutes = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (minutes < LAST_SEEN_PRECISION_MINUTES) return `Active in the last ${LAST_SEEN_PRECISION_MINUTES} minutes`;
  if (minutes < 60) return `Last active ${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 12) return `Last active ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (d.getFullYear() === yesterday.getFullYear() && d.getMonth() === yesterday.getMonth() && d.getDate() === yesterday.getDate()) {
    return `Last active yesterday, ${signedOutAtWords(d, d)}`;
  }
  return `Last active ${signedOutAtWords(d, now)}`;
}

/** "Signed in with Password" · "Signed in with Passkey and Email code" · "Signed in with an Email link". */
export function signInMethodWords(amr: readonly string[] | null | undefined): string {
  const list = (amr || []).filter(Boolean);
  if (!list.length) return "";
  const factors = list.filter((a) => (FACTOR_TYPES as readonly string[]).includes(a));
  const shown = (factors.length ? factors : list).map((a) => authWords("factor", a));
  const joined = shown.length > 1 ? `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}` : shown[0];
  return `Signed in with ${factors.length ? "" : "an "}${joined}`;
}

/** "IP 203.0.113.7 (AU)" — the place only when the server already knew it; never looked up here. */
export function whereWords(session: Pick<SignInSessionView, "ipAddress" | "country">): string {
  if (!session.ipAddress) return "IP address not seen yet";
  return session.country ? `IP ${session.ipAddress} (${session.country})` : `IP ${session.ipAddress}`;
}
